import { Flex, Text } from '@weareredlight/design-system'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo } from 'react'
import { Views } from 'react-big-calendar'

import type { HeaderProps } from 'react-big-calendar'
import type {
  AppointmentType,
  OrganizationAppointmentsParams,
} from 'types/appointments'
import type { OrganizationType } from 'types/organizations'
import type { PaginatedRequest } from 'types/types'

import AppointmentDetails from '../Appointments/AppointmentDetails'

import api from 'api/api'
import BigCalendar from 'components/BigCalendar'
import { DateHeader } from 'components/BigCalendar/DateHeader'
import Card from 'components/Card'
import ScheduleFilters from 'components/Schedule/ScheduleFilters'
import { useHolidays } from 'hooks/useHolidays'
import { useRequest } from 'hooks/useRequest'
import { CalendarViews, HolidayTypes, TreatmentStatuses } from 'utils/enums'
import { getName } from 'utils/text'

type CalendarViewType = {
  selectedOrg: OrganizationType
  defaultDate: string | Date
  view: CalendarViews
  showFilters: boolean
  calendarFilters?: OrganizationAppointmentsParams
  setCalendarFilters: (filters?: OrganizationAppointmentsParams) => void
}

const CalendarView = ({
  selectedOrg,
  defaultDate,
  showFilters,
  view,
  calendarFilters,
  setCalendarFilters,
}: CalendarViewType) => {
  const holidayParams = useMemo(() => {
    if (!selectedOrg.id) return undefined
    return { organizationId: selectedOrg.id }
  }, [selectedOrg.id])

  const { holidays, isLoading: isLoadingHolidays } = useHolidays(holidayParams)

  const {
    data: appointments,
    doRequest: getAppointments,
    isLoading: isLoadingAppointments,
  } = useRequest<
    PaginatedRequest<AppointmentType>,
    OrganizationAppointmentsParams
  >(api.getAppointments)

  const fetchAppointments = useCallback(() => {
    getAppointments({
      ...calendarFilters,
      organizationId: selectedOrg.id,
      startDateTime:
        view === CalendarViews.DAY
          ? dayjs(defaultDate).format('YYYY-MM-DDT00:00:00')
          : dayjs(defaultDate).startOf('week').format('YYYY-MM-DDT00:00:00'),
      endDateTime:
        view === CalendarViews.DAY
          ? dayjs(defaultDate).format('YYYY-MM-DDT23:59:59')
          : dayjs(defaultDate).endOf('week').format('YYYY-MM-DDT23:59:59'),
    })
  }, [getAppointments, selectedOrg, calendarFilters, defaultDate, view])

  const getHolidayForSlot = (slot: Date) => {
    return holidays?.find(
      holiday =>
        dayjs(slot).isSameOrAfter(dayjs(holiday.beginDateTime)) &&
        dayjs(slot).isSameOrBefore(dayjs(holiday.endDateTime)),
    )
  }

  const slotPropGetter = (date: Date) => {
    const holiday = getHolidayForSlot(date)

    if (!holiday) return {}

    const backgroundColor =
      holiday.type === HolidayTypes.HOLIDAY ? '#fff7f0' : '#f2f2f2'
    return {
      style: {
        backgroundColor,
      },
    }
  }

  // update appointments when filters change
  useEffect(() => {
    fetchAppointments()
    // update appointments every 15 minutes
    const interval = setInterval(() => {
      fetchAppointments()
    }, 300000)
    return () => clearInterval(interval)
  }, [fetchAppointments])

  const getEventType = (event: AppointmentType) => {
    const procedure = event?.treatmentProcedures?.[0].procedure
    if (!procedure) {
      return { className: '' }
    }
    return {
      className: procedure?.isCellBank ? 'cell-bank-event' : '',
    }
  }

  const appointmentsAsEvents: AppointmentType[] = useMemo(() => {
    if (!appointments) return []

    const events = appointments.data
      .filter(appointment => {
        return appointment.treatmentProcedures?.some(
          tp => tp?.treatment?.status !== TreatmentStatuses.CANCELLED,
        )
      })
      .map(appointment => ({
        ...appointment,
        start: dayjs(appointment?.startTime).toDate(),
        end: dayjs(appointment?.endTime).toDate(),
      }))

    return events
  }, [appointments])

  const eventComponent = useMemo(
    () => ({
      event: (event: { event: AppointmentType }) => {
        const appointmentEvent = event.event

        const procedureNames = appointmentEvent.treatmentProcedures
          ?.map(tp => tp?.procedure?.name)
          .filter(Boolean)
          .join(', ')

        return (
          <div className="event-details">
            <Flex direction="column" align="start">
              <Text variant="microCopy" color="white">
                {appointmentEvent.treatmentProcedures &&
                appointmentEvent.treatmentProcedures?.length > 0
                  ? getName(
                      appointmentEvent.treatmentProcedures?.[0].treatment,
                      'patient',
                    )
                  : '--'}
              </Text>
              <Text variant="microCopy" color="primary200">
                {String(procedureNames)}
              </Text>
            </Flex>
            <div className="event-trigger">
              <AppointmentDetails
                appointment={appointmentEvent}
                position={
                  view.toLocaleLowerCase() === Views.DAY ? 'bottom' : undefined
                }
              />
            </div>
          </div>
        )
      },
      header:
        holidays && holidays.length > 0
          ? (props: HeaderProps) => (
              <DateHeader {...props} holidays={holidays} />
            )
          : undefined,
    }),
    [view, holidays],
  )

  return (
    <>
      {showFilters && (
        <ScheduleFilters
          organizationId={selectedOrg.id}
          getAppointmentFilters={setCalendarFilters}
        />
      )}
      <Card extraClasses="wrapper full-width" isLoading={isLoadingAppointments}>
        {!isLoadingHolidays && (
          <BigCalendar
            date={defaultDate}
            events={appointmentsAsEvents}
            scrollToTime={dayjs(defaultDate).hour(8).minute(0).toDate()}
            onSelecting={() => false}
            draggableAccessor={() => false}
            toolbar={false}
            view={view?.toLocaleLowerCase()}
            style={{ height: 560 }}
            onNavigate={() => false}
            onView={() => false}
            eventPropGetter={getEventType}
            components={eventComponent}
            slotPropGetter={slotPropGetter}
          />
        )}
      </Card>
    </>
  )
}

export default CalendarView
